/* tslint:disable:variable-name */
import { Injectable, Inject } from '@angular/core';
import { Observable } from 'rxjs/internal/Observable';

import { ISchema, BasePersistentApiClient } from '../common/index';
import { BaseEntity, Field, BelongsTo, HasMany } from '../common/meta';
import {
    IInstanceCodeClient, instanceCodeClientT, paramsBuilderT,
    IParamsBuilder, IResource, IParam, IRecursiveArray
} from '../common/interfaces';

import {
    IConversionPixel, IConversionPixelClient, ConversionPixelBelongsToRelationships,
    ConversionPixelHasManyRelationships
} from './interfaces';
import { Advertiser } from '../advertiser/advertiser.entity';
import { IAdvertiser } from '../advertiser/interfaces';
import { PiggybackUrl } from '../piggyback-url/piggyback-url.service';
import { Pixel } from '../pixel/pixel.entity';
import { IPiggybackUrl } from '../piggyback-url/interfaces';
import { IPixel } from '../pixel/interfaces';
import { ICampaign } from '../campaign/interfaces';
import { Campaign } from '../campaign/campaign.entity';
import { Archivable } from '../common/archivable/archivable';
import { Activable } from '../common/activable/activable';
import { BulkRemovable } from '../common/bulk-removable/bulk-removable';
import { IBulkResponse } from './../common/errors';
import { IChangeable, IChangeCollection } from '../change/interfaces';
import { IComment, IPersistentComment } from '../comment/interfaces';
import { Changeable } from '../common/changeable/changeable';
import { AuthHttp } from '../authentication/authentication.service';
import { IRule } from '../../src/app/sdk.index';
import { Rule } from '@platform161-client/rule/rule.service';

export class ConversionPixel extends BaseEntity<IConversionPixel> implements IConversionPixel {

    public static readonly type: string = 'conversion_pixel';

    @Field()
    id: string;

    @Field()
    name: string;

    @Field()
    conversion_type: string;

    @Field()
    post_view_interval: number;

    @Field()
    post_click_interval: number;

    @Field()
    post_click_delivery_type: string;

    @Field()
    post_view_delivery_type: string;

    @Field()
    active: boolean;

    @Field()
    archived: boolean;

    @Field()
    deduplication: boolean;

    @Field()
    view_interval: number;

    @Field()
    view_delivery_type: string;

    @Field()
    postback_tracking: boolean;

    @Field()
    sync: boolean;

    @Field()
    loads: number;

    @Field()
    post_view_conversions: number;

    @Field()
    post_click_conversions: number;

    @Field()
    cross_device: boolean;

    @Field()
    landing: boolean;

    @Field()
    event_type: string;

    @Field()
    conversion_logic: string;

    @BelongsTo({ deferredConstructor: () => Advertiser })
    advertiser: IAdvertiser | IResource;

    @HasMany({ deferredConstructor: () => PiggybackUrl })
    piggyback_urls: IPiggybackUrl[] | IResource[];

    @HasMany({ deferredConstructor: () => Pixel, apiRelationshipName: 'piggybacks' })
    piggybacks: IPixel[] | IResource[];

    @HasMany({ deferredConstructor: () => Campaign })
    related_campaigns: ICampaign[] | IResource[];

    @HasMany({ deferredConstructor: () => Rule })
    rules: IRule[] | IResource[];
}

@Injectable()
@Archivable()
@Activable()
@BulkRemovable()
@Changeable()
export class ConversionPixelClient
    extends BasePersistentApiClient<IConversionPixel,
        ConversionPixelBelongsToRelationships,
        ConversionPixelHasManyRelationships>
    implements IConversionPixelClient, IChangeable {

    protected static classConfig: ISchema = {
        apiResourceName: 'conversion_pixels'
    };

    archive: (id: string) => Observable<IConversionPixel>;
    unarchive: (id: string) => Observable<IConversionPixel>;
    activate: (id: string) => Observable<IConversionPixel>;
    deactivate: (id: string) => Observable<IConversionPixel>;
    bulkRemove: (ids: string[]) => Observable<IBulkResponse[]>;

    constructor(
        http: AuthHttp,
        @Inject(instanceCodeClientT) instanceCodeClient: IInstanceCodeClient,
        @Inject(paramsBuilderT) paramsBuilder: IParamsBuilder
    ) {
        super(http, instanceCodeClient, paramsBuilder);
    }

    protected implementationClass() {
        return ConversionPixel;
    }

    getChanges(
        changeableId: string, params?: IParam, include?: IRecursiveArray<string>
    ): Observable<IChangeCollection> {
        throw new Error('Method not implemented.');
    }

    addChangeComment(note: IPersistentComment, changeableId: string): Observable<IComment> {
        throw new Error('Method not implemented.');
    }
}
